const DOCUMENT_URL_FIELDS = ['presigned_url', 'preview_url', 'url', 'document_url', 'file_url'];
const DOCUMENT_NAME_FIELDS = ['file_name', 'document_name', 'original_file_name'];
const DOCUMENT_QUESTION_TOKENS = ['file', 'upload', 'attachment', 'document', 'evidence'];

function normalize_text(value) {
  return String(value || '').trim();
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function first_truthy(values = []) {
  return values.find((value) => value !== null && value !== undefined && value !== '') ?? null;
}

function first_record_field(record = {}, fields = []) {
  return fields
    .map((field) => record?.[field])
    .find(Boolean) || null;
}

function normalize_record_fields(record = {}, fields = []) {
  return normalize_text(first_record_field(record, fields));
}

function format_date(value) {
  if (!value) {
    return 'Today';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return normalize_text(value);
  }

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function extension_from_filename(file_name = '') {
  const parts = normalize_text(file_name).split('.');
  return parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
}

function document_type_config(extension = '') {
  const normalized = normalize_text(extension).toLowerCase();
  const configs = [
    { matches: ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(normalized), tone: 'is-purple', icon: 'bi-image' },
    { matches: normalized === 'pdf', tone: 'is-red', icon: 'bi-file-earmark' },
  ];
  return configs.find((config) => config.matches) || { tone: 'is-blue', icon: 'bi-file-earmark-text' };
}

function document_tone(extension = '') {
  return document_type_config(extension).tone;
}

function document_icon(extension = '') {
  return document_type_config(extension).icon;
}

function first_document_url(item = {}) {
  return normalize_record_fields(item, DOCUMENT_URL_FIELDS);
}

function document_meta(extension = '', uploaded_label = '', section = '') {
  return [
    extension,
    uploaded_label,
    normalize_text(section),
  ].filter(Boolean).join(' - ');
}

function verification_state(item = {}) {
  const normalized_state = normalize_text(item?.verification_state).toLowerCase();
  if (['verified', 'rejected', 'pending'].includes(normalized_state)) {
    return normalized_state;
  }

  if (item?.is_verified === true) {
    return 'verified';
  }

  if (item?.verified_by_id != null) {
    return 'rejected';
  }

  return 'pending';
}

function verification_label(item = {}) {
  const state = verification_state(item);
  if (state === 'verified') return 'Verified';
  if (state === 'rejected') return 'Rejected';
  return 'Pending review';
}

function map_document(item = {}) {
  const title = document_item_title(item);
  const url = first_document_url(item);
  if (!has_document_title_or_url(title, url)) return null;

  const extension = extension_from_filename(first_truthy([title, url]));
  const uploaded_label = uploaded_document_meta(item?.submitted_on);
  const status_label = verification_label(item);

  return {
    id: document_item_id(item, title, url),
    additional_document_id: to_number(item?.additional_document_id || item?.id),
    application_id: to_number(item?.application_id),
    application_form_id: to_number(item?.application_form_id),
    question_id: to_number(item?.question_id),
    section_id: to_number(item?.section_id),
    section_question_id: to_number(item?.section_question_id),
    title: first_truthy([title, document_item_fallback_title(item)]),
    meta: document_meta(extension, uploaded_label, item?.section),
    icon: document_icon(extension),
    tone: document_tone(extension),
    extension,
    url,
    source: normalize_text(item?.source),
    section: normalize_text(item?.section),
    question: normalize_text(item?.question),
    submitted_on: item?.submitted_on || '',
    is_verified: item?.is_verified === true,
    verification_state: verification_state(item),
    verification_label: status_label,
    verification_comment: normalize_text(item?.verification_comment),
  };
}

function map_documents(raw_documents = []) {
  return (Array.isArray(raw_documents) ? raw_documents : []).map(map_document).filter(Boolean);
}

function has_document_title_or_url(title = '', url = '') {
  return Boolean(first_truthy([title, url]));
}

function document_item_title(item = {}) {
  return normalize_text(first_truthy([item?.file_name, item?.document_name, item?.file_url]));
}

function uploaded_document_meta(submitted_on) {
  return submitted_on ? `uploaded ${format_date(submitted_on)}` : '';
}

function document_item_id(item = {}, title = '', url = '') {
  return first_truthy([item?.id, `${title}-${first_truthy([item?.submitted_on, url])}`]);
}

function document_item_fallback_title(item = {}) {
  return normalize_text(first_truthy([item?.question, item?.section, 'Document']));
}

function raw_answer_document(answer = {}) {
  const raw_answer = answer?.raw_answer;
  return typeof raw_answer === 'object' && raw_answer !== null && !Array.isArray(raw_answer)
    ? raw_answer
    : {};
}

function is_document_question(answer = {}) {
  const question_type = normalize_text(answer?.question_type).toLowerCase();
  return DOCUMENT_QUESTION_TOKENS.some((token) => question_type.includes(token));
}

function has_document_payload(raw_object = {}) {
  return Boolean(first_record_field(raw_object, DOCUMENT_URL_FIELDS.concat(DOCUMENT_NAME_FIELDS)));
}

function is_document_answer(answer = {}, raw_object = {}) {
  return is_document_question(answer) || has_document_payload(raw_object);
}

function document_answer_title(answer = {}, raw_object = {}, url = '') {
  return normalize_text(first_truthy([
    raw_object?.file_name,
    raw_object?.document_name,
    raw_object?.original_file_name,
    answer?.answer_text,
    url,
  ]));
}

function uploaded_document_label(answer = {}, raw_object = {}) {
  return first_truthy([
    raw_object?.submitted_on,
    raw_object?.uploaded_at,
    raw_object?.date_created,
    raw_object?.created_at,
    answer?.submitted_on,
  ]);
}

function document_from_answer(answer = {}) {
  const document_data = document_answer_data(answer);
  if (!is_valid_document_data(document_data)) {
    return null;
  }

  return document_answer_record(document_data);
}

function document_answer_data(answer = {}) {
  const raw_object = raw_answer_document(answer);
  const url = first_document_url(raw_object);
  const title = document_answer_title(answer, raw_object, url);
  return {
    answer,
    raw_object,
    url,
    title,
    extension: extension_from_filename(title || url),
    is_document: is_document_answer(answer, raw_object),
  };
}

function is_valid_document_data(data = {}) {
  return Boolean(data.is_document && data.title && data.extension !== 'FILE');
}

function document_answer_record(data = {}) {
  const uploaded_label = uploaded_document_label(data.answer, data.raw_object);
  const section = document_answer_section(data.answer, data.raw_object);
  return {
    id: document_answer_id(data.answer, data.title, data.url),
    additional_document_id: null,
    application_id: to_number(data.answer?.application_id),
    application_form_id: to_number(data.answer?.application_form_id),
    question_id: to_number(data.answer?.question_id),
    section_id: to_number(data.answer?.section_id),
    section_question_id: to_number(data.answer?.section_question_id),
    title: data.title,
    meta: document_meta(data.extension, uploaded_document_meta(uploaded_label), section),
    icon: document_icon(data.extension),
    tone: document_tone(data.extension),
    extension: data.extension,
    url: data.url,
    source: normalize_text(first_truthy([data.raw_object?.source, 'application'])),
    section,
    question: normalize_text(data.answer?.question_text),
    submitted_on: uploaded_label || '',
    is_verified: data.raw_object?.is_verified === true,
    verification_state: verification_state(data.raw_object),
    verification_label: verification_label(data.raw_object),
    verification_comment: normalize_text(data.raw_object?.verification_comment),
  };
}

function document_answer_section(answer = {}, raw_object = {}) {
  return normalize_text(first_truthy([answer?.section_name, raw_object?.section, 'Documents']));
}

function document_answer_id(answer = {}, title = '', url = '') {
  const id_source = first_truthy([answer?.section_question_id, answer?.question_id, title]);
  return `${id_source}-${first_truthy([url, title])}`;
}

function split_answer_values(value = '') {
  return normalize_text(value)
    .split(',')
    .map((item) => normalize_text(item))
    .filter(Boolean);
}

function answer_options(answer = {}) {
  return Array.isArray(answer?.options)
    ? answer.options
      .map((option) => normalize_text(option?.choice || option?.label || option?.value || option))
      .filter(Boolean)
    : [];
}

function case_fact_item(answer = {}) {
  const label = answer.question_text || `Question ${answer.question_id || answer.section_question_id || ''}`;
  return {
    label,
    value: answer.answer_text,
    values: split_answer_values(answer.answer_text),
    question_type: normalize_text(answer?.question_type).toLowerCase(),
    options: answer_options(answer),
    section_question_id: to_number(answer?.section_question_id),
    question_id: to_number(answer?.question_id),
    application_form_id: to_number(answer?.application_form_id),
    document: document_from_answer(answer),
  };
}

function include_answer_in_case_facts(answer = {}, include_unanswered = false) {
  return include_unanswered || Boolean(normalize_text(answer?.answer_text));
}

function build_case_fact_items(answers = [], { include_unanswered = false, limit = 6 } = {}) {
  const mapped_answers = (Array.isArray(answers) ? answers : [])
    .filter((answer) => include_answer_in_case_facts(answer, include_unanswered));

  const limited_answers = Number.isFinite(Number(limit)) && Number(limit) >= 0
    ? mapped_answers.slice(0, Number(limit))
    : mapped_answers;

  return limited_answers
    .map(case_fact_item);
}

export {
  build_case_fact_items,
  document_from_answer,
  first_document_url,
  map_documents,
  split_answer_values,
};
